import {type FitViewOptions, useReactFlow} from '@xyflow/react';
import {type GenType, type ScreenSize} from '@features/flow/flowService';
import {getNodeDimensions} from '../utils/nodeDimensions';

const zoomIncrements = [0.01, 0.05, 0.1, 0.2, 0.5, 0.75, 1, 1.25, 2];

const sidebarWidth = 256; // Width of the sidebar in pixels
const padding = 64; // px

export const useViewport = () => {
  const reactFlow = useReactFlow();

  const zoomIn = () => {
    const currentZoom = reactFlow.getZoom();

    const firstBiggerZoom = zoomIncrements.find(
      (increment) => currentZoom < increment,
    );

    if (firstBiggerZoom === undefined) return;

    return reactFlow.zoomTo(firstBiggerZoom);
  };

  const zoomOut = () => {
    const currentZoom = reactFlow.getZoom();

    const firstSmallerZoom = zoomIncrements.findLast(
      (increment) => currentZoom > increment,
    );

    if (firstSmallerZoom === undefined) return;

    return reactFlow.zoomTo(firstSmallerZoom);
  };

  const fitViewToChat = (
    chatId: string,
    fitViewOptions: FitViewOptions = {
      duration: 300,
      interpolate: 'linear',
    },
  ) =>
    reactFlow.fitView({
      nodes: [{id: chatId}],
      padding: {
        right: `${padding}px`,
        left: `${sidebarWidth + padding}px`,
        y: `${padding}px`,
      },
      ...fitViewOptions,
    });

  const fitViewToAll = (
    fitViewOptions: FitViewOptions = {
      duration: 300,
      interpolate: 'linear',
    },
  ) =>
    reactFlow.fitView({
      padding: {
        right: `${padding}px`,
        left: `${sidebarWidth + padding}px`,
        y: `${padding}px`,
      },
      ...fitViewOptions,
    });

  /**
   * Center the viewport on a screen position using coordinates directly.
   * This avoids timing issues with fitView when the node doesn't exist yet.
   * Calculates zoom and position to match fitViewToChat behavior.
   */
  const centerOnPosition = (
    x: number,
    y: number,
    genType: GenType | ScreenSize,
  ) => {
    const {width, height} = getNodeDimensions(genType);
    const screenCenterX = x + width / 2;
    const screenCenterY = y + height / 2;

    // Calculate zoom to fit the screen with the same padding as fitViewToChat
    const leftPadding = sidebarWidth + padding;
    const rightPadding = padding;
    const verticalPadding = padding * 2;

    const zoomX = (window.innerWidth - leftPadding - rightPadding) / width;
    const zoomY = (window.innerHeight - verticalPadding) / height;
    const targetZoom = Math.min(zoomX, zoomY);

    // Adjust for sidebar using the target zoom level
    const sidebarOffsetInFlowCoords = sidebarWidth / (2 * targetZoom);

    return reactFlow.setCenter(
      screenCenterX - sidebarOffsetInFlowCoords,
      screenCenterY,
      {zoom: targetZoom, duration: 300},
    );
  };

  return {zoomIn, zoomOut, fitViewToChat, fitViewToAll, centerOnPosition};
};
